/**
 * 🐴 HeroStable — owner-driven mount swap panel.
 *
 * Twin of HeroWardrobe (different theme). Lists mounts the player owns
 * and lets them pick which one is READY for their active hero
 * (the one that contributes to BP via the Ash "mount term" per the
 * Oct 30 2026 Battle Power canon).
 *
 * Mount catalog is OWNER-PROVIDED. The empty state is intentional
 * until the owner delivers each mount.
 */
import { useMemo, useState } from "react";

// ─────────────────────── Mount catalog ───────────────────────
// 🖼️ Owner will add mounts here as each gets its art + binding:
//   {
//     id:        "ashstrider_1",
//     name:      "Ashstrider",
//     subtitle:  "Bram's bound mount",
//     art_url:   "https://.../ashstrider.webp",
//     binds_to:  ["bram"]        // null = universal
//   }
export const MOUNTS = [];

export default function HeroStable({ heroName, className }) {
  const available = useMemo(() => {
    const n = String(heroName || "").trim().toLowerCase().split(/[\s,]+/)[0];
    const c = String(className || "").trim().toLowerCase().split(/[\s,]+/)[0];
    return MOUNTS.filter((m) => {
      if (!m.binds_to || !m.binds_to.length) return true;
      return m.binds_to.includes(n) || m.binds_to.includes(c);
    });
  }, [heroName, className]);

  const [activeIdx, setActiveIdx] = useState(0);
  const active = available[activeIdx] || null;

  const prev = () => setActiveIdx((i) => (i - 1 + available.length) % available.length);
  const next = () => setActiveIdx((i) => (i + 1) % available.length);

  return (
    <div className="w-full flex flex-col items-center" data-testid="hero-stable">
      <div className="w-full flex items-center justify-between px-1 pb-2">
        <span className="text-[10px] uppercase tracking-widest text-amber-300/80">
          Stable
        </span>
        {available.length > 0 && (
          <span className="text-[9px] uppercase tracking-widest text-amber-400/60 font-mono">
            {activeIdx + 1} / {available.length}
          </span>
        )}
      </div>

      <div
        className="aspect-[3/4] w-full rounded-sm border-2 border-amber-700/40
                   bg-gradient-to-b from-[#1a1308] via-[#140d05] to-[#0b0703]
                   relative overflow-hidden"
        data-testid="hero-stable-panel"
      >
        {/* Stable-floor seam */}
        <div
          className="absolute inset-x-0 bottom-10 h-[2px] bg-gradient-to-r from-amber-900/0 via-amber-600/40 to-amber-900/0"
          aria-hidden="true"
        />

        {!active && (
          <div
            className="absolute inset-0 flex flex-col items-center justify-center text-center px-6"
            data-testid="hero-stable-empty"
          >
            <div className="text-4xl mb-3 opacity-70" aria-hidden="true">🐴</div>
            <div className="text-amber-300 text-sm font-bold uppercase tracking-widest">
              No mounts yet
            </div>
            <div className="text-amber-200/60 text-[11px] mt-2 leading-snug">
              Mounts will appear here as the owner adds them.
            </div>
          </div>
        )}

        {active && (
          <div
            className="absolute inset-0 flex items-end justify-center pb-4"
            data-testid="hero-stable-mount"
          >
            <img
              src={active.art_url}
              alt={active.name}
              className="max-h-[85%] max-w-[85%] object-contain drop-shadow-lg"
              data-testid="hero-stable-mount-art"
            />
          </div>
        )}

        {available.length > 1 && (
          <>
            <button
              onClick={prev}
              aria-label="Previous mount"
              data-testid="hero-stable-prev"
              className="absolute left-1 top-1/2 -translate-y-1/2
                         w-7 h-7 flex items-center justify-center
                         text-amber-300 text-lg font-bold rounded-sm
                         bg-black/50 border border-amber-700/50
                         hover:border-amber-400 hover:text-amber-100 transition"
            >
              ‹
            </button>
            <button
              onClick={next}
              aria-label="Next mount"
              data-testid="hero-stable-next"
              className="absolute right-1 top-1/2 -translate-y-1/2
                         w-7 h-7 flex items-center justify-center
                         text-amber-300 text-lg font-bold rounded-sm
                         bg-black/50 border border-amber-700/50
                         hover:border-amber-400 hover:text-amber-100 transition"
            >
              ›
            </button>
          </>
        )}
      </div>

      {active && (
        <div className="w-full mt-2 text-center" data-testid="hero-stable-mount-meta">
          <div className="text-amber-200 text-xs font-bold uppercase tracking-widest">
            {active.name}
          </div>
          {active.subtitle && (
            <div className="text-amber-300/60 text-[10px] tracking-wider mt-0.5">
              {active.subtitle}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
